// src/components/FGProduction/FGRecipesReports.jsx
//
// FG - Finished Goods Production (NEW, INDEPENDENT MODULE)
// -----------------------------------------------------------------------
// Shows only records belonging to the new FG Production module.
// Existing Finished Goods records are never displayed here.
// -----------------------------------------------------------------------

import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartBar, faSearch, faBoxOpen, faFlask } from '@fortawesome/free-solid-svg-icons';
import { usePopup } from '../context/PopupContext';
import api from '../../api';
import './FGProduction.css';
import { UTILITY_COST_RATE } from './fgProductionConstants';

const FGRecipesReports = () => {
  const { showPopup } = usePopup();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [search, setSearch] = useState('');

  const fetchReport = useCallback(async (deptId) => {
    try {
      setLoading(true);
      const res = await api.get('/fg-production/reports/recipes', {
        params: deptId ? { department_id: deptId } : {}
      });
      setRows(res.data || []);
    } catch (err) {
      console.error(err);
      showPopup('error', 'Failed to load FG Recipes Report');
    } finally {
      setLoading(false);
    }
  }, [showPopup]);

  useEffect(() => {
    api.get('/departments')
      .then(res => {
        const uniqueDepartments = Array.from(
          new Map(
            (Array.isArray(res.data) ? res.data : [])
              .filter(department => department && department.id != null)
              .filter(department => department.is_active !== false && department.deleted !== true)
              .map(department => [department.id, department])
          ).values()
        );
        setDepartments(uniqueDepartments);
      })
      .catch(err => console.error(err));
  }, [fetchReport]);

  useEffect(() => {
    fetchReport(selectedDepartment === 'all' ? undefined : selectedDepartment);
  }, [selectedDepartment, fetchReport]);

  const filteredRows = rows
    .filter(r => r.fg_item_name.toLowerCase().includes(search.toLowerCase()))
    .map(r => {
      const outputQty = parseFloat(r.recipe_output_quantity) || 0;
      const rmCost = parseFloat(r.rm_cost) || 0;

      const utilityPercentage = Number.parseFloat(r.utility_cost_percentage);
      const configuredUtilityPercentage = Number.isFinite(utilityPercentage)
        ? utilityPercentage
        : UTILITY_COST_RATE * 100;
      // Utility Cost = RM Cost × configured utility percentage
      const utilityCost = rmCost * (configuredUtilityPercentage / 100);
      // Total Production Cost = RM Cost + Utility Cost
      const totalProductionCost = rmCost + utilityCost;
      // Production Cost Per Piece = Total Production Cost ÷ Recipe Output Quantity
      const productionCostPerPiece = outputQty > 0 ? totalProductionCost / outputQty : null;

      const rmCostPerPiece = outputQty > 0 ? rmCost / outputQty : null;
      const sellingPrice = r.selling_price !== null && r.selling_price !== undefined
        ? parseFloat(r.selling_price)
        : null;
      const grossProfitPerPiece = (sellingPrice !== null && productionCostPerPiece !== null)
        ? sellingPrice - productionCostPerPiece
        : null;
      const grossMarginPct = (grossProfitPerPiece !== null && sellingPrice > 0)
        ? (grossProfitPerPiece / sellingPrice) * 100
        : null;

      return {
        ...r,
        rmCostPerPiece,
        productionCostPerPiece,
        grossProfitPerPiece,
        grossMarginPct
      };
    });

  const grandTotalCost = filteredRows.reduce((sum, r) => sum + (parseFloat(r.rm_cost) || 0), 0);
  const totalActiveRmRecipes = filteredRows.reduce((sum, r) => sum + (parseInt(r.rm_recipes_count) || 0), 0);

  return (
    <div className="fg-production-page">
      <header className="fg-production-header">
        <div>
          <div className="fg-production-breadcrumb">Finished Goods Production / Reports</div>
          <h1><FontAwesomeIcon icon={faChartBar} /> FG Recipes Reports</h1>
          <p className="fg-production-subtitle">Review recipe counts, production cost, selling price, and margin by item.</p>
        </div>
      </header>

      <div className="fg-production-card fg-production-card-body fg-production-filter">
        <div className="fg-production-filter-group">
          <label htmlFor="search-input">Search FG Item:</label>
          <div className="search-input-wrapper">
            <input
              id="search-input"
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Type to search..."
            />
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
          </div>
        </div>

        <div className="fg-production-filter-group">
          <label className="report-filter-label">Department</label>
          <select
            value={selectedDepartment}
            onChange={(e) => setSelectedDepartment(e.target.value)}
            className="report-filter-select"
          >
            <option value="all">All Departments</option>
            {departments.map(dept => (
              <option key={dept.id} value={dept.id}>{dept.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="fg-production-card">
        {loading ? (
          <div className="fgprod-loading">
            <div className="fgprod-spinner"></div>
            <span>Loading report...</span>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="fgprod-empty">
            <FontAwesomeIcon icon={faBoxOpen} size="3x" />
            <h3>No FG Production items found</h3>
          </div>
        ) : (
          <div className="fgprod-table-wrapper">
            <table className="fgprod-table fgprod-recipes-report-table">
              <colgroup>
                <col className="fgprod-recipes-col-item" />
                <col className="fgprod-recipes-col-department" />
                <col className="fgprod-recipes-col-output" />
                <col className="fgprod-recipes-col-recipes" />
                <col className="fgprod-recipes-col-cost-batch" />
                <col className="fgprod-recipes-col-cost-piece" />
                <col className="fgprod-recipes-col-production" />
                <col className="fgprod-recipes-col-selling" />
                <col className="fgprod-recipes-col-profit" />
                <col className="fgprod-recipes-col-margin" />
              </colgroup>
              <thead>
                <tr>
                  <th>FG Item Name</th>
                  <th>Department</th>
                  <th className="fgprod-report-center">Output (Pcs)</th>
                  <th className="fgprod-report-center">RM Recipes</th>
                  <th className="fgprod-report-currency">RM Cost</th>
                  <th className="fgprod-report-currency">RM Cost for Piece</th>
                  <th className="fgprod-report-currency">Production Cost for Piece</th>
                  <th className="fgprod-report-currency">Selling Price</th>
                  <th className="fgprod-report-currency">Gross Profit / Piece</th>
                  <th className="fgprod-report-center">Gross Margin %</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(row => (
                  <tr key={row.id}>
                    <td className="fgprod-bold" title={row.fg_item_name}>{row.fg_item_name}</td>
                    <td>{row.department_name || '-'}</td>
                    <td className="fgprod-report-center">{parseFloat(row.recipe_output_quantity || 0).toFixed(0)}</td>
                    <td className="fgprod-report-center fgprod-report-count">{row.rm_recipes_count}</td>
                    <td className="fgprod-report-currency fgprod-bold">
                      Rs. {parseFloat(row.rm_cost).toFixed(2)}
                    </td>
                    <td className="fgprod-report-currency fgprod-bold">
                      {row.rmCostPerPiece !== null
                        ? `Rs. ${row.rmCostPerPiece.toFixed(2)}`
                        : 'N/A'}
                    </td>
                    <td className="fgprod-report-currency fgprod-bold">
                      {row.productionCostPerPiece !== null
                        ? `Rs. ${row.productionCostPerPiece.toFixed(2)}`
                        : 'N/A'}
                    </td>
                    <td className="fgprod-report-currency fgprod-bold">
                      {row.selling_price !== null && row.selling_price !== undefined
                        ? `Rs. ${parseFloat(row.selling_price).toFixed(2)}`
                        : 'N/A'}
                    </td>
                    <td
                      className={`fgprod-report-currency fgprod-bold ${row.grossProfitPerPiece === null ? '' : row.grossProfitPerPiece >= 0 ? 'fgprod-positive' : 'fgprod-negative'}`}
                    >
                      {row.grossProfitPerPiece !== null
                        ? `Rs. ${row.grossProfitPerPiece.toFixed(2)}`
                        : 'N/A'}
                    </td>
                    <td
                      className={`fgprod-report-center fgprod-bold ${row.grossMarginPct === null ? '' : row.grossMarginPct >= 0 ? 'fgprod-positive' : 'fgprod-negative'}`}
                    >
                      {row.grossMarginPct !== null
                        ? `${row.grossMarginPct.toFixed(1)}%`
                        : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredRows.length > 0 && (
          <div className="fgprod-count">
            Showing {filteredRows.length} of {rows.length} FG Production item(s)
            {search && ` for "${search}"`}
          </div>
        )}
      </div>
    </div>
  );
};

export default FGRecipesReports;